import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { getBackendUrl } from '../../config/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  MessageCircle, Send, ArrowLeft, Search, User, Check, CheckCheck,
  Trash2, X, ChevronRight, Package, Maximize2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ChatDrawer() {
  const { token, user } = useAuth();
  const {
    drawerOpen, closeDrawer, openDrawer, activePartnerId, setActivePartnerId,
    listingContext, setListingContext, setUnreadCount, fetchUnreadCount, unreadCount,
  } = useChat();
  const navigate = useNavigate();

  const [conversations, setConversations] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [typingUser, setTypingUser] = useState(null);

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${getBackendUrl()}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setConversations(data.conversations);
      }
    } catch (e) { /* silent */ }
  }, [token]);

  // Fetch messages for a partner
  const fetchMessages = useCallback(async (partnerId) => {
    if (!token || !partnerId) return;
    try {
      const resp = await fetch(`${getBackendUrl()}/api/messages/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setMessages(data.messages);
        setSelectedPartner(data.partner);
        fetchUnreadCount();
        fetchConversations();
      }
    } catch (e) { /* silent */ }
  }, [token, fetchUnreadCount, fetchConversations]);

  // Open drawer -> fetch conversations
  useEffect(() => {
    if (drawerOpen && token) {
      fetchConversations();
      if (activePartnerId) {
        fetchMessages(activePartnerId);
      }
    }
  }, [drawerOpen, token, activePartnerId, fetchConversations, fetchMessages]);

  // Poll for new messages when chat is open
  useEffect(() => {
    if (!drawerOpen || !selectedPartner?.id) return;
    pollRef.current = setInterval(() => fetchMessages(selectedPartner.id), 5000);
    return () => clearInterval(pollRef.current);
  }, [drawerOpen, selectedPartner?.id, fetchMessages]);

  // Listen for WebSocket new message events
  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail;
      if (selectedPartner && (msg.sender_id === selectedPartner.id || msg.recipient_id === selectedPartner.id)) {
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        // Mark as read
        if (msg.sender_id === selectedPartner.id) {
          fetch(`${getBackendUrl()}/api/messages/${selectedPartner.id}/read`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => {});
        }
      }
      fetchConversations();
    };

    const typingHandler = (e) => {
      if (e.detail?.user_id === selectedPartner?.id) {
        setTypingUser(selectedPartner.name);
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setTypingUser(null), 3000);
      }
    };

    window.addEventListener('chat-new-message', handler);
    window.addEventListener('chat-typing', typingHandler);
    return () => {
      window.removeEventListener('chat-new-message', handler);
      window.removeEventListener('chat-typing', typingHandler);
    };
  }, [selectedPartner, token, fetchConversations]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Send message
  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedPartner || sending) return;

    setSending(true);
    try {
      const body = {
        recipient_id: selectedPartner.id,
        content: newMessage.trim(),
      };
      if (listingContext?.id) body.listing_id = listingContext.id;

      const resp = await fetch(`${getBackendUrl()}/api/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (resp.ok) {
        const data = await resp.json();
        setMessages(prev => [...prev, data.message]);
        setNewMessage('');
        setListingContext(null); // Clear listing context after first message
        fetchConversations();
      }
    } catch (e) { console.error('Send failed:', e); }
    finally { setSending(false); }
  };

  // Start conversation from gear listing
  const handleStartConversation = async () => {
    if (!newMessage.trim() || !activePartnerId || !listingContext?.id || sending) return;

    setSending(true);
    try {
      const resp = await fetch(`${getBackendUrl()}/api/messages/start-conversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          seller_id: activePartnerId,
          listing_id: listingContext.id,
          message: newMessage.trim(),
        }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setMessages(prev => [...prev, data.message]);
        setNewMessage('');
        fetchMessages(activePartnerId);
        fetchConversations();
      }
    } catch (e) { console.error('Start conversation failed:', e); }
    finally { setSending(false); }
  };

  // Search users
  const searchUsers = async (query) => {
    if (!query.trim()) { setSearchResults([]); return; }
    try {
      const resp = await fetch(`${getBackendUrl()}/api/messages/search-users?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setSearchResults(data.users);
      }
    } catch (e) { /* silent */ }
  };

  const selectConversation = (partnerId) => {
    setActivePartnerId(partnerId);
    fetchMessages(partnerId);
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const goBack = () => {
    setSelectedPartner(null);
    setActivePartnerId(null);
    setListingContext(null);
    setMessages([]);
  };

  const openFullPage = () => {
    closeDrawer();
    navigate('/messages');
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 86400000);
    if (diff === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (!token) return null;

  const isNewConversation = activePartnerId && listingContext && messages.length === 0;

  return (
    <>
      {/* Floating chat button - always visible when logged in */}
      {!drawerOpen && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          onClick={openDrawer}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-cyan-500 hover:bg-cyan-400 text-white rounded-full shadow-lg shadow-cyan-500/30 flex items-center justify-center transition-colors"
          data-testid="floating-chat-btn"
          title="Messages"
        >
          <MessageCircle className="w-6 h-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-rose-500 rounded-full text-xs flex items-center justify-center font-bold px-1">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </motion.button>
      )}

      <AnimatePresence>
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]"
            onClick={closeDrawer}
            data-testid="chat-drawer-backdrop"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full sm:w-[420px] bg-zinc-900 border-l border-white/10 z-[61] flex flex-col shadow-2xl"
            data-testid="chat-drawer"
          >
            {/* Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                {selectedPartner && (
                  <button onClick={goBack} className="p-1 hover:bg-white/10 rounded-lg transition-colors" data-testid="chat-back-btn">
                    <ArrowLeft className="w-5 h-5 text-zinc-400" />
                  </button>
                )}
                {selectedPartner ? (
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                      {selectedPartner.avatar_url ? (
                        <img src={selectedPartner.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <User className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <span className="font-semibold text-sm">{selectedPartner.name}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-cyan-400" />
                    <span className="font-bold">Messages</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={openFullPage} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" title="Open full page" data-testid="chat-fullpage-btn">
                  <Maximize2 className="w-4 h-4 text-zinc-400" />
                </button>
                <button onClick={closeDrawer} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="chat-close-btn">
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
            </div>

            {selectedPartner ? (
              /* Chat View */
              <>
                {/* Listing context banner */}
                {listingContext && (
                  <div className="px-4 py-2 bg-cyan-500/10 border-b border-cyan-500/20 flex items-center gap-3 shrink-0">
                    {listingContext.image_url && (
                      <img src={listingContext.image_url.startsWith('http') ? listingContext.image_url : `${getBackendUrl()}/api/gear/images/${listingContext.image_url}`} alt="" className="w-10 h-10 rounded-lg object-cover" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-cyan-400 truncate">{listingContext.title}</p>
                      {listingContext.price && <p className="text-xs text-zinc-400">${Number(listingContext.price).toLocaleString()}</p>}
                    </div>
                    <Package className="w-4 h-4 text-cyan-400 shrink-0" />
                  </div>
                )}

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.length === 0 && !isNewConversation && (
                    <div className="text-center py-12 text-zinc-500">
                      <p className="text-sm">No messages yet. Say hello!</p>
                    </div>
                  )}
                  {isNewConversation && (
                    <div className="text-center py-8 text-zinc-500">
                      <Package className="w-10 h-10 mx-auto mb-3 text-cyan-400/50" />
                      <p className="text-sm mb-1">Start a conversation about</p>
                      <p className="font-semibold text-white text-sm">{listingContext.title}</p>
                    </div>
                  )}
                  {messages.map((msg) => {
                    const isOwn = msg.sender_id === user?.id;
                    return (
                      <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        <div className={`group max-w-[80%]`}>
                          {msg.listing_context && !isOwn && (
                            <div className="mb-1 px-3 py-1.5 bg-white/5 rounded-lg text-xs flex items-center gap-2">
                              <Package className="w-3 h-3 text-cyan-400" />
                              <span className="truncate text-zinc-400">Re: {msg.listing_context.title}</span>
                            </div>
                          )}
                          <div className={`px-3.5 py-2 rounded-2xl text-sm ${
                            isOwn ? 'bg-cyan-500 text-white rounded-br-md' : 'bg-white/10 rounded-bl-md'
                          }`}>
                            <p className="break-words">{msg.content}</p>
                          </div>
                          <div className={`flex items-center gap-1.5 mt-0.5 text-[10px] text-zinc-500 ${isOwn ? 'justify-end' : ''}`}>
                            <span>{formatTime(msg.created_at)}</span>
                            {isOwn && (msg.read ? <CheckCheck className="w-3 h-3 text-cyan-400" /> : <Check className="w-3 h-3" />)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {typingUser && (
                    <div className="flex justify-start">
                      <div className="px-3 py-2 bg-white/5 rounded-2xl text-xs text-zinc-400 animate-pulse">
                        typing...
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={isNewConversation ? (e) => { e.preventDefault(); handleStartConversation(); } : handleSend} className="p-3 border-t border-white/10 shrink-0">
                  <div className="flex gap-2">
                    <Input
                      placeholder={isNewConversation ? "Write your message..." : "Type a message..."}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      className="flex-1 bg-white/5 border-white/10 text-sm h-10"
                      maxLength={2000}
                      data-testid="chat-message-input"
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      size="sm"
                      className="bg-cyan-500 hover:bg-cyan-600 h-10 px-3"
                      data-testid="chat-send-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              /* Conversations List View */
              <>
                <div className="p-3 border-b border-white/10 shrink-0">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSearch(!showSearch)}
                      className="border-white/10 text-xs"
                      data-testid="chat-new-btn"
                    >
                      {showSearch ? <X className="w-3.5 h-3.5" /> : 'New Chat'}
                    </Button>
                  </div>

                  {showSearch && (
                    <div className="mt-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                        <Input
                          placeholder="Search users..."
                          value={searchQuery}
                          onChange={(e) => { setSearchQuery(e.target.value); searchUsers(e.target.value); }}
                          className="pl-9 bg-white/5 border-white/10 text-sm h-9"
                          autoFocus
                          data-testid="chat-user-search"
                        />
                      </div>
                      {searchResults.length > 0 && (
                        <div className="mt-1 bg-white/5 rounded-lg max-h-32 overflow-y-auto">
                          {searchResults.map((u) => (
                            <button
                              key={u.id}
                              onClick={() => selectConversation(u.id)}
                              className="w-full p-2.5 flex items-center gap-2.5 hover:bg-white/5 transition-colors text-left text-sm"
                              data-testid={`chat-user-${u.id}`}
                            >
                              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shrink-0">
                                <User className="w-3.5 h-3.5 text-white" />
                              </div>
                              <span className="font-medium truncate">{u.username || u.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto">
                  {loading ? (
                    <div className="p-4 text-center text-zinc-500 text-sm">Loading...</div>
                  ) : conversations.length === 0 ? (
                    <div className="p-8 text-center">
                      <MessageCircle className="w-10 h-10 mx-auto mb-3 text-zinc-600" />
                      <p className="text-zinc-400 text-sm mb-1">No conversations yet</p>
                      <p className="text-xs text-zinc-500">Start a new chat or message a seller!</p>
                    </div>
                  ) : (
                    conversations.map((conv) => (
                      <button
                        key={conv.partner_id}
                        onClick={() => selectConversation(conv.partner_id)}
                        className={`w-full p-3 flex items-center gap-3 hover:bg-white/5 transition-colors border-b border-white/5`}
                        data-testid={`chat-conv-${conv.partner_id}`}
                      >
                        <div className="relative shrink-0">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                            {conv.partner_avatar ? (
                              <img src={conv.partner_avatar} alt="" className="w-full h-full rounded-full object-cover" />
                            ) : (
                              <User className="w-5 h-5 text-white" />
                            )}
                          </div>
                          {conv.unread_count > 0 && (
                            <span className="absolute -top-1 -right-1 w-4.5 h-4.5 min-w-[18px] bg-cyan-500 rounded-full text-[10px] flex items-center justify-center font-bold px-1">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-sm truncate">{conv.partner_name}</span>
                            <span className="text-[10px] text-zinc-500 shrink-0">{formatTime(conv.last_message_at)}</span>
                          </div>
                          <p className="text-xs text-zinc-400 truncate">{conv.last_message}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
    </>
  );
}
